import React, { useRef, useState, useEffect } from 'react';
import { X, BarChart2, LineChart as LineChartIcon, ToggleLeft, ToggleRight, Info, Clock, Star } from 'lucide-react';
import Highcharts from 'highcharts';
import Multichart from './Multichart';
import MChart from './MChart';
import FinancialMetricChart from './FinancialMetricChart';
import MetricSearch from './MetricSearch';
import CompanySearch from './CompanySearch';
import useViewportHeight from '../../helper/useViewportHeight';
import StockService from '../../Api_Module/Api_Services/StockService';

// Additional overlay metrics
const overlayMetrics: Metric[] = [{
  id: 'institutional_ownership',
  name: 'Institutional Ownership',
  category: 'Market Data',
  description: 'Percentage of shares held by institutional investors',
  tags: ['ownership', 'investors'],
  data: [{
    period: "Q1'22",
    value: 185000
  }, {
    period: "Q2'22",
    value: 195000
  }, {
    period: "Q3'22",
    value: 210000
  }, {
    period: "Q4'22",
    value: 215000
  }, {
    period: "Q1'23",
    value: 220000
  }]
}, {
  id: 'trading_volume',
  name: 'Trading Volume',
  category: 'Market Data',
  description: 'Total number of shares traded in a given period',
  tags: ['volume', 'trading'],
  data: [{
    period: "Q1'22",
    value: 45000
  }, {
    period: "Q2'22",
    value: 52000
  }, {
    period: "Q3'22",
    value: 48000
  }, {
    period: "Q4'22",
    value: 65000
  }, {
    period: "Q1'23",
    value: 58000
  }]
}, {
  id: 'rsi',
  name: 'RSI',
  category: 'Technical Indicators',
  description: 'Relative Strength Index, a momentum oscillator',
  tags: ['indicators', 'momentum'],
  data: [{
    period: "Q1'22",
    value: 65
  }, {
    period: "Q2'22",
    value: 58
  }, {
    period: "Q3'22",
    value: 72
  }, {
    period: "Q4'22",
    value: 45
  }, {
    period: "Q1'23",
    value: 62
  }]
}];
const metricCategories = [{
  id: 'balance_sheet',
  name: 'Balance Sheet',
  icon: BarChart2
}, {
  id: 'income_statement',
  name: 'Income Statement',
  icon: LineChartIcon
}, {
  id: 'cash_flow',
  name: 'Cash Flow',
  icon: BarChart2
}, {
  id: 'market_data',
  name: 'Market Data',
  icon: LineChartIcon
}, {
  id: 'technical',
  name: 'Technical Indicators',
  icon: BarChart2
}];
const colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6'];
type OverlayChartType = 'bar' | 'line';

interface CompanyMetricPair {
  company: string;
  metric: Metric;
  color: string;
}

interface ChartEngineProps {
  availableMetrics?: Metric[];
  onMetricSelect?: (metric: Metric) => void;
  selectedPairs?: CompanyMetricPair[];
  onMetricRemove?: (pairId: string) => void;
  onChangePeriod?: (period: string) => void;
  currentPeriod?: string;
  // New: time-range preset selector (1Y / 3Y / 5Y / All)
  onChangeRange?: (range: '1Y' | '3Y' | '5Y' | 'ALL') => void;
  currentRange?: '1Y' | '3Y' | '5Y' | 'ALL';
  activeCompany: string;
  onSelectCompany?: (company: string) => void;
  showChart?: boolean;
};

const ChartAnalysis: React.FC<ChartEngineProps> = ({ availableMetrics = [], onMetricSelect, selectedPairs = [], onMetricRemove, onChangePeriod, currentPeriod = 'annual', onChangeRange, currentRange = 'ALL', activeCompany, onSelectCompany, showChart = true }) => {
  // Derived metrics from selectedPairs to maintain backwards compatibility
  const selectedMetrics: Metric[] = (selectedPairs || []).map(p => p.metric);

  const [overlayEnabled, setOverlayEnabled] = useState(false);
  const [selectedOverlay, setSelectedOverlay] = useState<string>('institutional_ownership');
  const [overlayChartType, setOverlayChartType] = useState<OverlayChartType>('bar');
  const [showScaleWarning, setShowScaleWarning] = useState(false);

  // Comparison company state: store fetched metrics for overlaying
  const [comparison, setComparison] = useState<{ symbol: string; name?: string; metrics: Metric[] } | null>(null);

  const categories = [
    { id: 'income', name: 'Income Statement', icon: '📊' },
    { id: 'balance', name: 'Balance Sheet', icon: '⚖️' },
    { id: 'cash', name: 'Cash Flow Statement', icon: '💰' },
    { id: 'ratios', name: 'Ratios', icon: '⏱️' },
    { id: 'segments', name: 'Segments & KPIs', icon: '📈' }
  ];

  const periods: any = selectedMetrics.length > 0 ? selectedMetrics[0]?.data?.map((d: any) => d.xaxisLabel).reverse() : [];

  const getYIndex = (all: Metric[], percent: boolean, point: boolean) => {
    if (all.length <= 1) return 0;
    let result: number | undefined = undefined;
    let percentIndex: number | undefined = undefined;
    let pointIndex: number | undefined = undefined;
    let dollarIndex: number | undefined = undefined;
    all.forEach((m: any, i) => {
      if (m.data && m.data[0] && m.data[0].percent && percentIndex === undefined) percentIndex = i;
      if (m.data && !m.data[0]?.formattedValue?.includes('B') && !m.data[0]?.percent && pointIndex === undefined) pointIndex = i;
      if (m.data && m.data[0]?.formattedValue?.includes('B') && !m.data[0]?.percent && dollarIndex === undefined) dollarIndex = i;
    });
    if (percent) result = percentIndex;
    if (point) result = pointIndex;
    if (!percent && !point) result = dollarIndex;
    return result;
  }

  const [companySearchTerm, setCompanySearchTerm] = useState('');

  const handleSelectMetric = (metric: Metric) => {
    const isAlreadySelected = selectedMetrics.find(m => m.metricAttributeName === metric.metricAttributeName);

    if (isAlreadySelected) {
      handleRemoveMetric(metric.metricAttributeName ?? '');
    } else if (selectedMetrics.length < 5) {
      onMetricSelect?.(metric);
      // Auto-clear company search to allow next company selection
      setCompanySearchTerm('');
    }
  };

  const handleRemoveMetric = (metricId: string) => {
    onMetricRemove?.(metricId);
  };

  const handleToggleOverlay = () => {
    setOverlayEnabled(!overlayEnabled);
  };

  const handleOverlayChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedOverlay(e.target.value);
  };

  return <div className="bg-theme-secondary rounded-lg p-3 relative">
    <div className="sticky top-0 z-40 -m-3 p-3 bg-theme-secondary/95 backdrop-blur supports-[backdrop-filter]:backdrop-blur">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <CompanySearch
          placeholder="Search company"
          value={companySearchTerm}
          onChange={setCompanySearchTerm}
          onCompanySelect={(c) => {
            onSelectCompany?.(c.symbol || '');
            // Keep the selected company name in the input until a metric is selected
            setCompanySearchTerm(c.name || c.symbol || '');
          }}
        />
        <MetricSearch
          symbol={activeCompany}
          availableMetrics={availableMetrics}
          selectedMetrics={selectedPairs.map(p => p.metric)}
          onMetricSelect={handleSelectMetric}
          onMetricRemove={handleRemoveMetric}
          containerClassName="mb-0"
        />
      </div>

      {/* Period Toggle + Selected Metrics header + Range selector */}
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] text-theme-muted">Selected Metrics</div>
        <div className="flex items-center gap-2">
          {/* Period toggle */}
          <div className="inline-flex items-center rounded-full overflow-hidden text-[11px] border border-theme-tertiary bg-theme-secondary">
            <button
              onClick={() => onChangePeriod?.('annual')}
              className={`px-3 py-1 font-medium transition-colors ${currentPeriod === 'annual'
                ? 'bg-[var(--btn-active-bg)] text-[var(--btn-active-text)]'
                : 'bg-transparent text-theme-muted'
                }`}
            >
              Annual
            </button>
            <button
              onClick={() => onChangePeriod?.('quarter')}
              className={`px-3 py-1 font-medium transition-colors ${currentPeriod === 'quarter'
                ? 'bg-[var(--btn-active-bg)] text-[var(--btn-active-text)]'
                : 'bg-transparent text-theme-muted'
                }`}
            >
              Quarterly
            </button>
          </div>

          {/* Time-range preset selector */}
          <div className="inline-flex items-center rounded-full overflow-hidden text-[11px] border border-theme-tertiary bg-theme-secondary">
            {(['1Y', '3Y', '5Y', 'ALL'] as const).map((preset) => (
              <button
                key={preset}
                onClick={() => onChangeRange?.(preset)}
                className={`px-2.5 py-1 font-medium transition-colors ${currentRange === preset
                  ? 'bg-[var(--btn-active-bg)] text-[var(--btn-active-text)]'
                  : 'bg-transparent text-theme-muted'
                  }`}
              >
                {preset === 'ALL' ? 'All' : preset}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-nowrap gap-1.5 overflow-x-auto pb-1 text-[11px]">
        {selectedPairs.map((pair) => {
          return (
            <div
              key={`${pair.company}-${pair.metric.metricAttributeName}`}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs bg-theme-secondary/80 border border-theme-tertiary text-theme-primary shadow-sm"
            >
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: pair.color }}
              />
              <span className="whitespace-nowrap">
                {pair.company} - {pair.metric.label}
              </span>
              <button
                onClick={() => handleRemoveMetric(`${pair.company}-${pair.metric.metricAttributeName}`)}
                className="text-theme-muted hover:text-theme-primary flex-shrink-0"
                title="Remove"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </div>

    {overlayEnabled && <div className="bg-theme-tertiary/50 p-2 rounded-md mb-2 text-xs">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div>
          <label className="block text-xs text-theme-muted mb-1">
            Overlay Metric
          </label>
          <select value={selectedOverlay} onChange={handleOverlayChange} className="w-full bg-theme-tertiary border border-theme-tertiary rounded-md text-xs text-theme-primary p-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500">
            {overlayMetrics.map(metric => <option key={metric.metricAttributeName} value={metric.metricAttributeName}>
              {metric.label}
            </option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-theme-muted mb-1">
            Chart Type
          </label>
          <div className="flex">
            <button onClick={() => setOverlayChartType('bar')} className={`flex items-center justify-center px-2 py-1.5 text-xs rounded-l-md ${overlayChartType === 'bar' ? 'bg-blue-600 text-theme-primary' : 'bg-theme-tertiary text-theme-muted'} hover:bg-theme-tertiary/70`}>
              <BarChart2 size={14} className="mr-1" />
              Bar
            </button>
            <button onClick={() => setOverlayChartType('line')} className={`flex items-center justify-center px-2 py-1.5 text-xs rounded-r-md ${overlayChartType === 'line' ? 'bg-blue-600 text-theme-primary' : 'bg-theme-tertiary text-theme-muted'} hover:bg-theme-tertiary/70`}>
              <LineChartIcon size={14} className="mr-1" />
              Line
            </button>
          </div>
        </div>
        {showScaleWarning && <div className="p-1.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-md flex items-center">
          <span>Scale difference may affect visualization</span>
        </div>}
      </div>
    </div>}

    {showChart && selectedMetrics.length > 0 ? (
      <div className="w-full" ref={chartAnchorRef} style={{ height: dynamicHeight }}>
        {overlayEnabled ? null : (
          <>
            {/* <Multichart seriesData={seriesData ? seriesData : []} periods={periods} /> */}
            <MChart height={dynamicHeight} sliderVisible={false} rangeVisible={false} seriesData={seriesData ? seriesData : []} onChange={onChangePeriod} periods={periods} />
          </>
        )}
      </div>
    ) : showChart ? (
      <div className="h-[350px] flex items-center justify-center border border-dashed border-theme-tertiary rounded-md">
        <div className="text-center text-theme-muted">
          <p className="mb-2">Search and select metrics to display</p>
          <p className="text-xs">You can compare up to 5 metrics</p>
        </div>
      </div>
    ) : null}

    {/* Mobile view - responsive adjustments */}
    <div className="md:hidden mt-2 text-xs text-theme-muted text-center">
      Tip: Rotate device for better chart view
    </div>
  </div>;
}

export default ChartAnalysis;